import { Injectable } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { ALLOWED_TAGS, ALLOWED_ATTR } from "../shared/globals";

@Injectable({
    providedIn: "root",
})
export class SanitiseService {
    disallowedTags: Set<string> = new Set();

    constructor(private domSanitizer: DomSanitizer) {
        // Hook into DOMPurify to capture disallowed tags
        DOMPurify.addHook("uponSanitizeElement", (node, hookEvent) => {
            if (!hookEvent.allowedTags[node.nodeName.toLowerCase()]) {
                this.disallowedTags.add(node.nodeName.toLowerCase());
            }
        });

        // Hook into DOMPurify to capture disallowed attributes
        DOMPurify.addHook("uponSanitizeAttribute", (element, hookEvent) => {
            if (!hookEvent.allowedAttributes[hookEvent.attrName]) {
                this.disallowedTags.add(`${element.nodeName.toLowerCase()}[${hookEvent.attrName}]`);
            }
        });
        DOMPurify.addHook("beforeSanitizeAttributes", (element) => {
            // console.log("Tag ", node.tagName);
            if (!this.isSafeSrc(element)) {
                element.removeAttribute("src");
            }
        });
    }

    private isSafeSrc(element: Element): boolean {
        const tag = element.tagName?.toLowerCase();
        if (!(tag === "iframe" || tag === "img")) return true;

        const src = element.getAttribute("src");
        //console.log("Src: ", src, DOMPurify.isValidAttribute(tag, 'src', src), this.isSafeUrl(src));
        if (src && !(DOMPurify.isValidAttribute(tag, "src", src) && this.isSafeUrl(src))) {
            return false;
        }
        return true;
    }

    sanitise(htmlString: string): string {
        this.disallowedTags.clear();
        const cleanHtml: string = DOMPurify.sanitize(htmlString, {
            ALLOWED_TAGS: ALLOWED_TAGS,
            ALLOWED_ATTR: ALLOWED_ATTR,
            WHOLE_DOCUMENT: true, // Allow sanitizing of the whole document, including <head>
        });
        return cleanHtml;
    }

    htmlToSave(htmlString: string) {
        return this.sanitise(htmlString);
    }

    htmlToDisplay(htmlString: string): SafeHtml {
        const cleanHtml: string = this.sanitise(htmlString);
        return this.domSanitizer.bypassSecurityTrustHtml(cleanHtml);
    }

    getUnsafeHtml(htmlString: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(htmlString);
    }

    getDisallowedTags(): string[] {
        return Array.from(this.disallowedTags);
    }

    private isSafeUrl(url: string): boolean {
        const allowedProtocols = ["http:", "https:", "data:"];
        try {
            const parsedUrl = new URL(url, window.location.href);

            // Check if the protocol is allowed
            if (!allowedProtocols.includes(parsedUrl.protocol)) {
                return false;
            }

            // Additional check to ensure the URL does not contain any script
            const scriptPattern = /<script[\s\S]*?>[\s\S]*?<\/script\s*>/gi;
            if (scriptPattern.test(url)) {
                return false;
            }

            // Ensure the URL does not contain `javascript:` scheme
            if (parsedUrl.href.toLowerCase().startsWith("javascript:")) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }
}
